// Shapes from docs/presentation-package-v1-contract.md. Imports in this folder use explicit
// `.ts` extensions so the Node tools can run the same modules with type stripping.

export interface FileRef { path: string; sha256: string }

export type FormFactor = 'any' | 'phone' | 'desktop';

/** A `presentations[]` entry. `variant` marks it player-selectable under that id (docs/ui-variants-design.md). */
export interface DistributionPresentation {
  path: string;
  sha256: string;
  default?: boolean;
  variant?: string;
  label?: string;
  summary?: string;
  formFactor?: FormFactor;
}

export interface Distribution {
  formatVersion: 'distribution-1';
  id: string;
  version: string;
  title: string;
  rules: { id: string; label: string; path: string; sha256: string; default?: boolean }[];
  presentations: DistributionPresentation[];
}

export interface AssetEntry {
  id: string;
  path: string;
  type: 'image/png' | 'image/jpeg' | 'image/webp';
  width: number;
  height: number;
  sha256: string;
  license: string;
  attribution?: string;
  fallback: string;
}

export type Capability = 'cards' | 'board-graph' | 'board-regions' | 'allocation' | 'ordering' | 'images';

export interface Manifest {
  formatVersion: 'presentation-1';
  id: string;
  version: string;
  label: string;
  compatible: { gameId: string; rulesVersions: string[]; ui: 'ui-2' };
  requires: Capability[];
  files: { layouts: FileRef; templates: FileRef; interactions: FileRef; theme: FileRef };
  assets: AssetEntry[];
}

export type Gap = 'none' | 's' | 'm' | 'l';

export type ComponentName = 'zone' | 'board' | 'counters' | 'seat-header' | 'status' | 'choices' | 'inspector' | 'log' | 'result';

export type LayoutNode =
  | { type: 'row' | 'column'; children: LayoutNode[]; weights?: number[]; gap?: Gap }
  | { type: 'grid'; columns: number; children: LayoutNode[] }
  | { type: 'tabs'; tabs: { label: string; child: LayoutNode }[] }
  | { type: 'repeat'; over: 'seats' | 'opponents'; order: 'self-first' | 'seat-order'; child: LayoutNode }
  | { type: 'panel'; title?: string; child: LayoutNode; collapsible?: boolean }
  | { type: 'component'; component: ComponentName; select?: string; props?: Record<string, unknown> };

export interface Layouts { layouts: { wide: LayoutNode; narrow?: LayoutNode } }

export type Shape = 'card' | 'token' | 'tile' | 'space' | 'node' | 'edge';

export interface Template {
  id: string;
  match: { kind?: string; catalog?: string[]; tags?: string[]; zoneKind?: string; face?: 'up' | 'down' };
  shape: Shape;
  slots: Record<string, string>;
}

export interface Templates { templates: Template[] }

export type ControlName = 'list' | 'buttons' | 'in-place' | 'ordered' | 'payment' | 'stepper' | 'textarea';

/**
 * Options of a pick-list layout. `describe: "adjacent-regions"` adds a line under each move or option that names a board node or
 * edge, listing the regions that touch it ("Beside Grain, roll 4 · Sea"), derived from the Describe board and the observed region
 * objects. Display only: it adds no information beyond the board and the public observation.
 */
export interface PickListsOptions { describe?: 'adjacent-regions' }

export interface Interactions {
  groups?: { group: string; order?: number; hotkey?: string }[];
  controls?: { match: { type?: string; field?: string; keyPrefix?: string; group?: string; hasObjects?: boolean }; control: ControlName }[];
  quickSubmit?: boolean;
  /** Direct play marks targets playable without paying (Free) and defaults a pick to its only free option. */
  preferFree?: boolean;
  /**
   * A layout without the pieces' board (a phone hand): an object pick is answered from the step's option list instead of by
   * clicking the table, because the objects are not drawn.
   */
  pickLists?: boolean | PickListsOptions;
  /**
   * Action kinds whose choices open as one form (every pick and allocation at once, one submit) instead of
   * step by step; choices of such a kind at one anchor that differ only in bounds merge into that form.
   */
  forms?: string[];
  /**
   * A per-viewer convenience: when the only choice available is one stepless command of a listed kind (an optional response
   * window with nothing to respond with), the client sends that command after a short uniform delay unless the viewer turned it
   * off. The viewer's switch is local to the browser and on by default.
   */
  autoAnswer?: { kind: string; delayMs?: number; label?: string }[];
  /**
   * A readout in the prompt bar for choices whose group starts with `group`: how much a cost asks for (the numeric observed counter
   * `counter`) and how much the selected objects supply (the sum of their numeric `<fieldPrefix><unit>` fields, per unit with its icon).
   * Display only; the package's own checks and Apply decide whether a selection pays.
   */
  tally?: TallyRule[];
  /**
   * Choice groups that answer another player's move (a reaction window, a held roll). While the viewer's available choices include
   * one of them, the turn bar's lead reads "Your response" instead of "Your turn".
   */
  responseGroups?: string[];
}

export interface TallyRule { group: string; counter: string; fieldPrefix: string; units: string[]; noun?: string }

export const COLOR_TOKENS = ['surface', 'surface-raised', 'ink', 'ink-muted', 'accent', 'danger', 'selected', 'focus'] as const;
export type ColorToken = typeof COLOR_TOKENS[number];

export interface Theme {
  colors?: Partial<Record<ColorToken, string>> & { dark?: Partial<Record<ColorToken, string>> };
  seatColors?: Record<string, string>;
  icons?: Record<string, string>;
  maps?: Record<string, Record<string, string>>;
  font?: 'sans' | 'serif' | 'rounded';
  cardSize?: number;
}

export const SHAPE_SLOTS: Record<Shape, string[]> = {
  card: ['title', 'subtitle', 'corner', 'cost', 'art', 'band', 'text', 'footer', 'badge'],
  token: ['label', 'art', 'fill', 'badge'],
  tile: ['label', 'art', 'fill', 'badge'],
  space: ['label', 'art', 'fill', 'badge'],
  node: ['label', 'art', 'fill', 'badge'],
  edge: ['fill', 'label', 'badge'],
};

export const BUILTIN_GLYPHS = ['circle', 'square', 'triangle', 'diamond', 'star', 'hex', 'drop', 'leaf', 'stone', 'berry', 'twig', 'resin', 'coin', 'card', 'worker', 'point'] as const;

/** A loaded, verified presentation package ready for the client. */
export interface Presentation {
  manifest: Manifest;
  layouts: Layouts;
  templates: Templates;
  interactions: Interactions;
  theme: Theme;
  /** Asset ID to URL. */
  assetUrls: Map<string, string>;
}

// ---- presentation-2 (docs/presentation-package-v2-contract.md) ---------------------------

export type CapabilityV2 = Capability | 'scene' | 'direct-play' | 'motion' | 'materials' | 'faces' | 'atlases' | 'hosted-art' | 'complex-board' | 'records' | 'pixi-board' | 'edge-pieces' | 'rich-text' | 'catalog-guide' | 'seat-ring' | 'three-board' | 'three-helpers';

export type FileKeyV2 = 'scene' | 'targets' | 'templates' | 'interactions' | 'theme' | 'layouts' | 'faces' | 'atlases' | 'art' | 'boards';

/** A verified glTF binary the 3D board may load (three-board). `bytes` and `sha256` are checked before parsing. */
export interface ModelEntry { id: string; path: string; sha256: string; bytes: number; license: string; attribution?: string }

/**
 * A baked standee file the 3D board loads on demand (three-helpers). Generated by build-distribution from the .standee
 * file and presentation.config.json; the display fields are resolved (entry, standeeDefaults, preset, built-in default).
 */
export interface StandeeManifestEntry {
  id: string; path: string; sha256: string; bytes: number;
  front: string; artBounds: [number, number, number, number];
  height: number; mounting: 'upright' | 'ground'; facing: 'fixed' | 'camera'; edge: string;
}
/** Every field is required; the single source for the validator and the docs test. */
export const STANDEE_MANIFEST_FIELDS = { required: ['id', 'path', 'sha256', 'bytes', 'front', 'artBounds', 'height', 'mounting', 'facing', 'edge'], optional: [] } as const;

export interface ManifestV2 {
  formatVersion: 'presentation-2';
  module?: FileRef & { api: 'ui-sdk-1' };
  models?: ModelEntry[];
  standees?: StandeeManifestEntry[];
  id: string;
  version: string;
  label: string;
  compatible: { gameId: string; rulesVersions: string[]; ui: 'ui-2' };
  requires: CapabilityV2[];
  /** `scene` and `theme` are required; the rest are optional. */
  files: Partial<Record<FileKeyV2, FileRef>> & { scene: FileRef; theme: FileRef };
  assets: AssetEntry[];
  artRelease?: import('../art/release.ts').ArtRef;
}

/** One brick placed in a scene area, or a `repeat` over seats. */
export interface BrickInstance {
  brick: string;
  /** Counter selectors on `repeat` restrict seats to observed counter owners. */
  select?: string;
  /** Seat for seat-scoped bricks such as `seat-mat`: `{self}`, `{seat}` or a seat ID. */
  seat?: string;
  props?: Record<string, unknown>;
  /** `repeat` only. */
  over?: 'seats' | 'opponents';
  order?: 'self-first' | 'seat-order';
  child?: BrickInstance;
}

export interface Scene {
  /** Optional shared viewport shell; omitted scenes retain their authored grid. */
  shell?: 'compact-table';
  /** Public decorative image per named area. Images never replace live bricks. */
  backgrounds?: Record<string, string>;
  /** Opt-in intrinsic page layout; omitted keeps the existing fixed viewport grid. */
  flow?: 'page';
  minWidth?: number;
  areas: string[];
  columns: string[];
  rows: string[];
  place: Record<string, BrickInstance[]>;
  rest: string;
  /**
   * Board IDs this layout deliberately does not draw (a phone hand). Their zones are not sent to the `rest` area; the
   * observation, the choices and every other layout still carry them.
   */
  omitBoards?: string[];
  /** A touch layout: scene controls (tabs, prompt buttons, panels, chips) are at least 44 CSS pixels tall. */
  touch?: boolean;
  /** Tabbed views inside one area (contract section 7.4): only the chosen tab's bricks draw. */
  views?: Views;
  /** Where the turn prompt sits: in the HUD (`header`) or floating over the viewer's mat. Default: `header` with views, else `floating`. */
  prompt?: 'header' | 'floating';
  /** Surface of the floating prompt: `light` (default) or `dark` (follows the dark table). */
  promptTone?: 'light' | 'dark';
  /** Where a turn banner (the toast naming an opponent's move) appears: `top` under the header (default) or `corner`, the bottom right corner, clear of the board. */
  toast?: 'top' | 'corner';
}

export interface ViewTab {
  id: string;
  /** Tab text; under `perSeat` the text `{seat}` becomes the seat's label. */
  label: string;
  /** One key (1-9 or a-z, never the drawer key) that switches to this tab; a `perSeat` tab's key counts up per seat. */
  key?: string;
  /** Noun after the count when the tab holds choice targets, e.g. `playable` reads "5 playable". */
  badge?: string;
  /** Theme icon key shown beside the label in compact navigation. */
  icon?: string;
  /** Short explanation shown on hover and keyboard focus. */
  description?: string;
  /** One tab per opponent; `{seat}` in selectors and the label names that seat. */
  perSeat?: 'opponents';
  /** One navigation entry plus a labeled selector; leaf tabs/anchors remain unchanged. */
  groupLabel?: string;
  /** `targets`: show for lit targets; `zones`: show when a selected observed zone exists, even empty. */
  showWhen?: 'targets' | 'zones';
  /** Optional decorative public artwork, asset:ID. */
  background?: string;
  /** The tab only mirrors targets that other tabs also light (a list of the same territories): its lit anchors count in its badge but neither trigger nor block auto-focus. */
  passive?: boolean;
  bricks: BrickInstance[];
}

export interface Views {
  area: string;
  tabs: ViewTab[];
  /** `single-view-targets`: when every lit target of a step lives in one tab, show it. */
  autoFocus?: 'single-view-targets' | 'none';
  /**
   * `choices`: a tab's lit-count badge counts the distinct choices the step lights in it (not every lit anchor), shows nothing
   * when it lights none (no object counts), drops the noun on a phone and keeps the phone's tab strip compact and fading at the edge.
   */
  badges?: 'choices';
  /** Surface of the tab panel (default parchment) and its tint. */
  material?: Material;
  tint?: string;
}

export interface SceneFile {
  scene: Scene;
  narrow?: Scene;
  /** Drawn instead of `scene` (same width rules) when the table has exactly one seat, for example a cooperative game played alone. */
  solo?: Scene;
  guide?: import('../catalog/types.ts').CatalogGuide;
}

export interface TargetRule {
  match: { kind?: string; data?: Record<string, string | number | boolean>; group?: string };
  anchor: string;
  button?: string;
  /** Also offer the choice as a button in the idle action bar (moves with no object, such as ending a season). */
  bar?: boolean;
  /** Highlight this routed action while it is offered in the compact turn bar. */
  attention?: boolean;
  /** Route the choice by this rule even when its first step's options name objects (they stay pickable in the step). */
  priority?: boolean;
  /** Add this anchor alongside normal object/option routing; never replaces the primary targets. */
  also?: boolean;
}

export interface TargetsFile { targets: TargetRule[] }

export const MATERIALS = ['felt', 'wood', 'parchment', 'slate', 'linen', 'paper', 'night'] as const;
export type Material = typeof MATERIALS[number];
export const CARD_STYLES = ['classic', 'ornate', 'minimal', 'tarot'] as const;
export const FONT_NAMES = ['Fraunces', 'Cinzel', 'Alegreya Sans', 'Nunito', 'IBM Plex Mono'] as const;
export type FontName = typeof FONT_NAMES[number];
export const SEAT_PIECES = ['meeple', 'train', 'disc', 'cube', 'pawn'] as const;
export const TEXT_ROLES = ['title', 'subtitle', 'rules', 'flavor', 'number', 'caption'] as const;
export type TextRole = typeof TEXT_ROLES[number];

export interface TextRoleStyle { font?: 'display' | 'body' | 'mono' | FontName; weight?: number; size?: number; italic?: boolean; caps?: boolean }

export interface ThemeV2 extends Theme {
  material?: { table?: Material; tint?: string; mats?: Material; matTint?: string };
  cardStyle?: typeof CARD_STYLES[number];
  elevation?: 'flat' | 'soft' | 'deep';
  motion?: { speed?: 'calm' | 'brisk' | 'snappy'; opponentPace?: number };
  fonts?: { display?: FontName; body?: FontName };
  seatPieces?: Record<string, typeof SEAT_PIECES[number]>;
  textRoles?: Partial<Record<TextRole, TextRoleStyle>>;
}

/** A loaded, verified presentation-2 package. */
export interface PresentationV2 {
  format: 2;
  module?: import('../protocol/wire').PackageUiRef;
  manifest: ManifestV2;
  scene: SceneFile;
  targets: TargetsFile;
  templates: Templates;
  interactions: Interactions;
  theme: ThemeV2;
  layouts: Layouts | null;
  faces: import('../faces/types.ts').FacesFile | null;
  atlases?: import('../faces/types.ts').AtlasesFile | null;
  art?: import('../faces/types.ts').ArtFile | null;
  assetUrls: Map<string, string>;
  /** Model id to its served URL and expected hash and size (three-board). */
  models?: Map<string, { url: string; sha256: string; bytes: number }>;
  /** Standee id to its served URL, hash, size and resolved display defaults (three-helpers). */
  standees?: Map<string, import('../boards/three/standeeRuntime.ts').StandeeRef>;
  artRelease?: import('../art/release.ts').ArtRelease;
  artHash?: string;
  artNotice?: string;
  boards?: import('../boards/types.ts').BoardVisuals;
}
